import { createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

interface SurveySessionState {
  user: { token?: string; user?: { _id?: string; store?: string }; company?: { _id?: string } };
}
const SurveySessionContext = createContext<{
  queryKey: readonly unknown[]; token?: string; userId?: string; enabled: boolean;
  sessionKey: string; isCurrentSession: () => boolean;
} | null>(null);

export function ProductSurveySessionProvider({ children }: { children: React.ReactNode }) {
  const { token, user, company } = useSelector((state: SurveySessionState) => state.user);
  const { search } = useLocation();
  const storeId = new URLSearchParams(search).get('storeId') || user?.store;
  const client = useQueryClient();
  const instance = useId();
  const generation = useRef(0);
  const scope = useMemo(() => ({ token, userId: user?._id, companyId: company?._id, storeId, generation: ++generation.current }),
    [token, user?._id, company?._id, storeId]);
  const current = useRef(scope);
  const mounted = useRef(true);
  current.current = scope;
  const queryKey = useMemo(() => ['productSurveys', 'active', scope.userId, scope.companyId, scope.storeId, instance, scope.generation] as const, [scope, instance]);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    return () => { client.removeQueries({ queryKey, exact: true }); };
  }, [client, queryKey]);
  return <SurveySessionContext.Provider value={{ queryKey, token, userId: scope.userId,
    enabled: Boolean(token && scope.userId), sessionKey: `${instance}:${scope.generation}`,
    isCurrentSession: () => mounted.current && current.current === scope,
  }}>{children}</SurveySessionContext.Provider>;
}

export function useProductSurveyScope() {
  const scope = useContext(SurveySessionContext);
  if (!scope) throw new Error('ProductSurveySessionProvider is required');
  return scope;
}
